import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
  signal,
} from '@angular/core';
import { AbstractControl, FormArray, FormControl, FormGroup } from '@angular/forms';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Subject,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs';

import { SfoUiJSONSchema7 } from '../metadata.model';
import { SfoFormHelperService } from './form-helper.class';
import { findPropertySchema } from './service/form-build-helpers';
import { FormService } from './service/form.service';
import { SchemaKeywords, SfoUiWidgetType } from './types';
import { RenderMode } from './types/view.types';

@Component({
  selector: 'sfo-dynamic-form',
  templateUrl: './dynamic-form.component.html',
  styleUrls: ['./dynamic-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class DynamicFormComponent implements OnDestroy {
  private destroy$: Subject<void> = new Subject<void>();
  private formService: FormService = inject(FormService);
  private formValue$: BehaviorSubject<object> = new BehaviorSubject({});
  private cd: ChangeDetectorRef = inject(ChangeDetectorRef);

  schema$ = new BehaviorSubject<SfoUiJSONSchema7 | null>(null);

  formReady = signal<boolean>(false);
  formErrors = signal<string | null>(null);
  schemaErrors = signal<string | null>(null);

  /**
   * The same (validated, `removeAdditional`-filtered) patch object just
   * applied to `parentForm`, passed straight through to `<sfo-simple-view>`
   * so it can judge a `_ui_secondary` field's initial emptiness without
   * racing `handleFormPatch`'s `patchValue(patch, { emitEvent: false })` —
   * which never fires a `valueChanges` a consumer could sample instead.
   * Pure pass-through: nothing else here reacts to it.
   */
  appliedPatch = signal<object | null>(null);

  parentForm: FormGroup = new FormGroup({});

  /**
   * Sets the JSON schema and triggers a change detection in the component.
   * @param data - The JSON schema data to be set. It is cast to `SfoUiJSONSchema7` type.
   */
  @Input() set jsonSchema(data: unknown) {
    this.schema$.next(data as SfoUiJSONSchema7);
  }

  /**
   * The view type for the settings. This determines the layout or mode in which settings are displayed.
   * `default` - Vertical navigation using drawer
   * `tabbed` - Horizontal navigation using tabs
   * `simple` - Single column, max width
   * `quickSettings` - Single column, high level children are wrapped in panels
   * @type {RenderMode}
   * @default 'settings'
   */
  @Input() renderMode: RenderMode = 'default';

  /**
   * Opt-in field layout for `renderMode="simple"`: honours each property's
   * `_ui_class` width, groups by `_ui_group`, and collapses empty creator-role
   * arrays. Off by default so existing simple-mode forms are unchanged.
   */
  @Input() gridLayout = false;

  /**
   * Specifies which tab should be initially active when using 'tabbed' renderMode.
   * Can be either a tab index (number) or tab name (string).
   */
  @Input() activeTab?: string | number;

  /**
   * Patches the current internally generated FormGroup with a new value and triggers a change detection in the component.
   * @param formValue - The value to be set in the form. If null, no action is taken.
   */
  @Input() set patchForm(formValue: object | null) {
    if (!formValue) return;
    this.formValue$.next(formValue);
  }

  /**
   * Emits an event with the current `FormGroup`.
   * @type {EventEmitter<FormGroup>}
   */
  @Output() formValue = new EventEmitter<object>();

  /**
   * Emits an event when the patch is invalid
   * @type {EventEmitter<FormGroup>}
   */
  @Output() patchError = new EventEmitter<unknown | unknown[]>();

  constructor() {
    this.schema$
      .pipe(
        takeUntil(this.destroy$),
        tap(() => this.initializeForm()),
        filter(Boolean),
        filter(() => this.formReady() && !!this.parentForm),
        switchMap((schema) =>
          this.parentForm.valueChanges.pipe(
            filter(() => this.parentForm.valid && this.formReady() && this.parentForm.dirty),
            map((formValue) => {
              const cleanedFormValues = SfoFormHelperService.removeNullValues(formValue, schema);
              const cleanedDefaults = SfoFormHelperService.removeDefaults(
                cleanedFormValues,
                schema,
              );
              const cleanedAdditionalProperties =
                SfoFormHelperService.removeAdditionalPropertiesWithDefaults(
                  cleanedDefaults,
                  schema,
                );

              return cleanedAdditionalProperties;
            }),
          ),
        ),
      )
      .subscribe((processedValue) => {
        if (!processedValue) return;
        this.formValue.emit(processedValue);
      });

    combineLatest([this.schema$, this.formValue$])
      .pipe(
        takeUntil(this.destroy$),
        filter(([schema, patch]) => !!schema && !!patch && Object.keys(patch).length > 0),
        tap(([schema, patch]) => this.handleFormPatch(schema as SfoUiJSONSchema7, patch)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForm(): void {
    this.schemaErrors.set(null);
    this.formErrors.set(null);
    // A schema switch must never hand the view a patch that was applied to
    // the PREVIOUS schema's form: if the next patch fails validation against
    // the new schema, nothing else would clear this and the stale object
    // would drive the secondary-field emptiness capture for the new schema.
    this.appliedPatch.set(null);

    const schema = this.schema$.getValue();
    if (!schema) {
      this.schemaErrors.set('The schema provided is empty.');
      return;
    }

    this.formReady.set(false);

    if (this.parentForm) {
      this.parentForm.reset();
    }

    try {
      this.parentForm = this.formService.generateForm(schema);
      this.formReady.set(true);
    } catch (e) {
      console.error(e);
      this.formErrors.set(e?.['message']);
    }
    this.cd.markForCheck();
  }

  private handleFormPatch(schema: SfoUiJSONSchema7, patch: object): void {
    try {
      const isPatchValid = SfoFormHelperService.isPatchValid(schema, patch, { removeAdditional: true });
      if (!isPatchValid?.isValid) {
        this.patchError.emit(isPatchValid?.errors);
        return;
      }

      this.syncFormArrayControls(this.parentForm, patch as Record<string, unknown>, schema);
      this.syncAdditionalPropertyControls(
        this.parentForm,
        patch as Record<string, unknown>,
        schema,
      );

      this.formErrors.set(null);
      this.parentForm.patchValue(patch, { emitEvent: false });
      this.appliedPatch.set(patch);
      this.cd.markForCheck();
    } catch (e) {
      this.patchError.emit(e);
      this.formErrors.set(e);
      console.error(e);
    }
  }

  /**
   * Synchronizes FormArray controls by adjusting their length and recursively calling itself for FormGroup items.
   * This is based on the original syncFormArrayControls logic.
   * @param currentAbstractControl The current AbstractControl (FormGroup or FormArray) to process.
   * @param correspondingPatchData The patch data corresponding to this control.
   * @param rootSchema The root schema for finding property schemas.
   */
  private syncFormArrayControls(
    currentAbstractControl: AbstractControl,
    correspondingPatchData: Record<string, unknown>,
    rootSchema: SfoUiJSONSchema7,
  ): void {
    if (!(currentAbstractControl instanceof FormGroup) || !correspondingPatchData) {
      return;
    }
    const formGroup = currentAbstractControl;

    Object.keys(formGroup.controls).forEach((key) => {
      const control = formGroup.get(key);
      if (!control) return;

      const patchValueForControl = correspondingPatchData[key];

      if (control instanceof FormArray && Array.isArray(patchValueForControl)) {
        const formArray = control;
        const fullPath = this.formService.getControlPath(formGroup, key);
        if (!rootSchema) return;

        const propertySchema = findPropertySchema(rootSchema, fullPath);

        if (propertySchema && propertySchema.type === 'array' && propertySchema.items) {
          const itemsSchema = propertySchema.items as SfoUiJSONSchema7;
          const currentLength = formArray.length;
          const targetLength = patchValueForControl.length;

          if (targetLength > currentLength) {
            for (let i = currentLength; i < targetLength; i++) {
              let newArrayItemControl: AbstractControl;
              const itemPatchData: unknown = patchValueForControl[i];

              // Special handling for creator role arrays (authors, editors, etc.)
              if (
                (propertySchema as SfoUiJSONSchema7)?.[SchemaKeywords.UiWidget] ===
                SfoUiWidgetType.CreatorRoleArray
              ) {
                // Always create all three controls, but set appropriate values based on mode
                if (
                  itemPatchData &&
                  typeof itemPatchData === 'object' &&
                  itemPatchData !== null &&
                  'literal' in itemPatchData
                ) {
                  const typedItemPatch = itemPatchData as Record<string, unknown>;
                  // Literal mode: set literal value, nullify person fields
                  newArrayItemControl = new FormGroup({
                    family: new FormControl(null),
                    given: new FormControl(null),
                    literal: new FormControl(typedItemPatch.literal ?? ''),
                  });
                } else {
                  // Person mode: set person fields, nullify literal field
                  const typedItemPatch = itemPatchData as Record<string, unknown> | null;
                  newArrayItemControl = new FormGroup({
                    family: new FormControl(typedItemPatch?.family ?? ''),
                    given: new FormControl(typedItemPatch?.given ?? ''),
                    literal: new FormControl(null),
                  });
                }
              } else if (itemsSchema.type === 'object' || itemsSchema.properties) {
                newArrayItemControl = this.formService.buildFormGroup(
                  itemsSchema,
                  `${fullPath}[${i}]`,
                  itemPatchData as Record<string, unknown>,
                );
              } else if (itemsSchema.type === 'array' && itemsSchema.items) {
                newArrayItemControl = this.formService.buildFormArray(
                  itemsSchema,
                  `${fullPath}.${i}`,
                  itemPatchData as unknown[],
                );
              } else {
                newArrayItemControl = this.formService.buildFormControl(itemsSchema);
              }
              formArray.push(newArrayItemControl, { emitEvent: false });
            }
          } else if (targetLength < currentLength) {
            for (let i = currentLength - 1; i >= targetLength; i--) {
              formArray.removeAt(i, { emitEvent: false });
            }
          }

          patchValueForControl.forEach((itemPatch: unknown, index: number) => {
            const arrayItemControl = formArray.at(index);
            if (
              arrayItemControl instanceof FormGroup &&
              typeof itemPatch === 'object' &&
              itemPatch !== null
            ) {
              this.syncFormArrayControls(
                arrayItemControl,
                itemPatch as Record<string, unknown>,
                rootSchema,
              );
            } else if (arrayItemControl instanceof FormArray && Array.isArray(itemPatch)) {
              // Handle nested FormArray case (like date-parts)
              const nestedFormArray = arrayItemControl;
              const targetLength = itemPatch.length;
              const currentLength = nestedFormArray.length;

              // Adjust the nested array length
              if (targetLength > currentLength) {
                for (let i = currentLength; i < targetLength; i++) {
                  nestedFormArray.push(new FormControl(itemPatch[i]), { emitEvent: false });
                }
              } else if (targetLength < currentLength) {
                for (let i = currentLength - 1; i >= targetLength; i--) {
                  nestedFormArray.removeAt(i, { emitEvent: false });
                }
              }

              // Patch the values
              if (Array.isArray(itemPatch)) {
                itemPatch.forEach((value: unknown, nestedIndex: number) => {
                  const control = nestedFormArray.at(nestedIndex);
                  if (control instanceof FormControl) {
                    control.setValue(value, { emitEvent: false });
                  }
                });
              }
            }
          });
        }
      } else if (
        control instanceof FormGroup &&
        patchValueForControl &&
        typeof patchValueForControl === 'object' &&
        patchValueForControl !== null
      ) {
        this.syncFormArrayControls(
          control,
          patchValueForControl as Record<string, unknown>,
          rootSchema,
        );
      }
    });
  }

  /**
   * Creates controls for properties found in the patch data if they don't exist in the formGroup
   * and are allowed by the schema's `additionalProperties`.
   * Recursively calls itself for nested FormGroups.
   * @param formGroup The FormGroup to process.
   * @param patch The patch data corresponding to this FormGroup.
   * @param groupSchema The schema for this FormGroup.
   */
  private syncAdditionalPropertyControls(
    formGroup: FormGroup,
    patch: Record<string, unknown>,
    groupSchema: SfoUiJSONSchema7,
  ): void {
    if (!formGroup || typeof patch !== 'object' || patch === null || !groupSchema) return;

    Object.keys(patch).forEach((key) => {
      const patchValue: unknown = patch[key];
      let control = formGroup.get(key);
      let propertySchemaForControl: SfoUiJSONSchema7 | undefined;

      if (groupSchema.properties?.[key]) {
        propertySchemaForControl = groupSchema.properties[key] as SfoUiJSONSchema7;
      }

      if (!control) {
        if (groupSchema.additionalProperties) {
          let schemaForNewProperty: SfoUiJSONSchema7 | undefined;
          if (typeof groupSchema.additionalProperties === 'object') {
            schemaForNewProperty = groupSchema.additionalProperties as SfoUiJSONSchema7;
          } else if (groupSchema.additionalProperties === true) {
            if (
              typeof patchValue === 'object' &&
              !Array.isArray(patchValue) &&
              patchValue !== null
            ) {
              schemaForNewProperty = { type: 'object', properties: {} } as SfoUiJSONSchema7;
            } else if (Array.isArray(patchValue)) {
              schemaForNewProperty = {
                type: 'array',
                items: { type: 'string' },
              } as SfoUiJSONSchema7;
            } else {
              schemaForNewProperty = { type: typeof patchValue } as SfoUiJSONSchema7;
            }
          }

          if (schemaForNewProperty) {
            let newControlToAdd: AbstractControl | undefined;
            if (
              schemaForNewProperty.type === 'object' ||
              (!schemaForNewProperty.type && schemaForNewProperty.properties)
            ) {
              newControlToAdd = this.formService.buildFormGroup(schemaForNewProperty);
            } else if (schemaForNewProperty.type === 'array' && schemaForNewProperty.items) {
              const fullPath = this.formService.getControlPath(formGroup, key);
              newControlToAdd = this.formService.buildFormArray(
                schemaForNewProperty,
                fullPath,
                patchValue,
              );
            } else {
              newControlToAdd = this.formService.buildFormControl(schemaForNewProperty);
            }

            if (newControlToAdd) {
              formGroup.addControl(key, newControlToAdd, { emitEvent: false });
              control = newControlToAdd;
              propertySchemaForControl = schemaForNewProperty;
            }
          }
        }
      }

      if (control instanceof FormGroup && typeof patchValue === 'object' && patchValue !== null) {
        const schemaForRecursion =
          propertySchemaForControl ||
          (groupSchema.properties?.[key] as SfoUiJSONSchema7) ||
          (typeof groupSchema.additionalProperties === 'object'
            ? (groupSchema.additionalProperties as SfoUiJSONSchema7)
            : undefined) ||
          (groupSchema.additionalProperties === true
            ? ({ type: 'object', properties: {} } as SfoUiJSONSchema7)
            : undefined);
        if (schemaForRecursion && typeof patchValue === 'object' && patchValue !== null) {
          this.syncAdditionalPropertyControls(
            control,
            patchValue as Record<string, unknown>,
            schemaForRecursion,
          );
        }
      }
    });
  }
}
